import i18next from "i18next";
import { withBreadCrumbs } from "lego-gui-lib";
import { observer } from "mobx-react-lite";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  Route,
  RouteComponentProps,
  Switch,
  useHistory,
  useRouteMatch
} from "react-router";
import { authSchemaHelper } from "../../core/model/authSchemaHelper";
import {
  CreateMessageSchema,
  customerMessagesContext,
  EditMessageSchema,
  messageContext,
  MessageSchema
} from "./schema";
import { CreateView, IndexView, ShowView } from "lego-gui-lib/lib";
import {
  MessageDataIndex,
  MessageDataCreate,
  MessageDataEdit
} from "./data/views";
import { ParentRoute } from "../../core/model/parent";
import { NestedView } from "../../common/nestedViewComponent";
import { CustomerAwareRowButtons } from "../../common/CustomerAwareRowButtons";
import { authStoreContext, mainStore } from "../../core/mainStore";
import { isAllowed } from "../../common/common";

export const Messages = {
  url: "/messages",
  handler: (props: RouteComponentProps<any>) => {
    const title = i18next.t("hns:message:domain_plural");
    const baseUrl = props.match.url;

    return withBreadCrumbs(
      { to: baseUrl, children: title },
      <>
        <ParentRoute path={baseUrl + "/:id"} dataSourceName={"message"} />
        <Switch>
          <Route exact={true} path={baseUrl} component={Index} />
          <Route
            path={baseUrl + "/:id/data/create"}
            component={MessageDataCreate}
          />
          <Route
            path={baseUrl + "/:id/data/:dataId"}
            component={MessageDataEdit}
          />
          <Route path={baseUrl + "/create"} component={Create} />
          <Route path={baseUrl + "/:id"} component={Show} />
        </Switch>
      </>
    );
  }
};

const IndexSchema = MessageSchema.extend({
  customerRef: { if: () => mainStore.auth.isAdmin() }
});

export const Index = observer(({ ...props }: any) => {
  const message = useContext(messageContext);
  const customerMessages = useContext(customerMessagesContext);
  const auth = useContext(authStoreContext);

  const i18n = useTranslation();
  const title = i18n.t("hns:message:domain", { count: 2 });

  return (
    <IndexView
      dataSource={
        auth.isAnyCustomerOwner() ? customerMessages : message.listDataSource
      }
      {...props}
      title={title}
      toolbarButtons={{ delete: true, export: true }}
      rowButtons={(rowButtonProps: any) =>
        CustomerAwareRowButtons(rowButtonProps, { copy: false })
      }
      columns={IndexSchema}
    />
  );
});

export const Create = observer(({ ...rest }: any) => {
  const message = useContext(messageContext);

  const match = useRouteMatch();
  const baseUrl = match.url;
  const history = useHistory();

  const i18n = useTranslation();
  const title = i18n.t("common:create", { domain: "hns:message:domain" });

  return withBreadCrumbs(
    { to: baseUrl + "/create", children: title },
    <CreateView
      dataSource={message.oneDataSource}
      {...rest}
      initialValues={authSchemaHelper.prepareInitialValuesForAnyCustomerOwnerCreate(
        message,
        { charged: 1 }
      )}
      title={title}
      fields={CreateMessageSchema}
      id={match.params}
      goBack={() => history.goBack()}
    />
  );
});

export const Show = observer(({ ...rest }) => {
  const match = useRouteMatch();
  const message = useContext(messageContext);
  const i18n = useTranslation();
  const baseUrl = match.url;
  const allowed = isAllowed(message);
  const title = allowed
    ? i18n.t("common:edit", { domain: "hns:message:domain" })
    : i18n.t("hns:message:domain");
  const history = useHistory();

  return withBreadCrumbs(
    { to: baseUrl + "/edit", children: title },
    <NestedView
      showView={
        <ShowView
          dataSource={message.oneDataSource}
          hideDeleteButton={!allowed}
          title={title}
          fields={
            allowed ? EditMessageSchema : EditMessageSchema.makeAllReadonly()
          }
          id={match.params}
          preloadData={false}
          onlyView={!allowed}
          goBack={() => history.goBack()}
        />
      }
      indexView={
        <MessageDataIndex match={match} {...rest} isAllowed={allowed} />
      }
    />
  );
});
